import * as angular from 'angular';
import L from 'leaflet';
import Map from '../../map/map';
import commentModalContent from '../comment-modal/commentModalContent.html';
import featureIssueModalHtml from './feature-issue-modal-template.html';
import featuretypeModalHtml from './featuretype-modal-template.html';
import issueModalHtml from './issue-modal-template.html';

import FeatureLayers from '../contract-features/contract-features-featureLayers';
import server from '../../../integration/server/server-services';
import contractService from '../../../service/contract/contract-services';
import datacatalog from '../../../integration/datacatalog/datacatalog-services';
import security from '../../../service/security/security-services';
import nvdbapi from '../../../integration/nvdbapi/nvdbapi';
import {AUTH_EVENTS} from '../../../service/security/security-module';
import {UserSession} from "../contract-features-3/domain";
import {CompletenessItem} from "../../../integration/representation/feature-type";
import {Comment, Destination} from "../../../integration/representation/contract";
import {FeatureByTypeId} from "../../../model/model-featurelist";
import {Feature} from "../../../integration/representation/feature";
import {ContractFile} from "../../../integration/representation/file";
import {IssueResolution, Location, ValidationIssue} from "../../../integration/representation/validation-issue";

interface CommonStatusControllerState {
    accordion: { completenessIsOpen: boolean };
    nvdbDeletions: DeletedFeature[];
    mapDisplay: string;
    toggleMap: Function;
    contract: contractService;
    completenessItems: DisplayCompletenessItem[];
    featureLayers: any; // This is the FeatureLayers function, which should be defined as a class
    showFeatures: Function;
    showDeletions: Function;
    showGuide: boolean;
    highlightedFeatures: string[];
    openFeatureTypeModal: Function;
    init: Function;
    allFeatureTypeComments: any;
    commentFeatureType: Function;
    files: {name: string, issues: ValidationIssue[]}[];
    getFeatureTypeName: Function;
    getFeatureUri: Function;
    toggleHighlightDeletion: Function;
    isHighlighted: Function;
}

interface ContractStatusControllerState extends CommonStatusControllerState {
    canViewShareableLink: boolean;
    canResolveIssue: boolean;
    canCommentContract: boolean;
    canDeleteDeletion: boolean;
    showSidemenu: boolean;

    openIssueModal: Function;
    openFeatureIssueModal: Function;
    removeDeletion: Function;
    openCommentModal: Function;
}

interface StatusRootScope extends angular.IRootScopeService {
    contractname: string;
}

interface DisplayCompletenessItem extends CompletenessItem {
    hasNvdb?: boolean;
    hasFkb?: boolean;
    issues?: ValidationIssue[];
    comments?: DisplayFeatureComment[];
    hasIssues?: boolean;
    issuesByMessage?: IssuesByMessage[];
}

interface IssueGroup {
    featureTypes: Location[];
    issueIds: string[];
    issues: ValidationIssue[];
    severity: string;
    locations: Location[];
}

interface IssuesByMessage {
    message: string;
    locations: Location[];
    severity: string;
    issueIds: string[];
    issues: ValidationIssue[];
}

interface DisplayFeatureComment {
    feature: {id: string},
    name: string,
    comment: string,
    error: boolean
}

interface DeletedFeature extends Feature {
    typeName?: string;
}

function hasIssues(feature: DisplayCompletenessItem) {
    const fkbMissing = feature.fkbFeatureTypeName.length > 0 && feature.fkbObjectCount === 0;
    const nvdbMissing = feature.nvdbFeatureTypeName.length > 0 && feature.nvdbObjectCount === 0;

    return fkbMissing || nvdbMissing || feature.issues.length > 0;
}

function groupIssuesByMessage(issues: ValidationIssue[]): IssuesByMessage[] {
    const collector: { [key: string]: IssueGroup} = {};
    angular.forEach(issues, function (issue) {
        if (!collector[issue.message]) {
            collector[issue.message] = {
                locations: [],
                severity: issue.severity,
                issueIds: [],
                issues: [],
                featureTypes: []
            };
        }
        collector[issue.message].featureTypes.push(issue.location);
        collector[issue.message].issueIds.push(issue.id);
        collector[issue.message].issues.push(issue);
    });


    return Object.keys(collector).map(function (message) {
        const values = collector[message];
        return {
            message,
            locations: values.featureTypes,
            severity: values.severity,
            issueIds: values.issueIds,
            issues: values.issues
        };
    });
}
function permissionGranted(user: UserSession, requiredPermission: string, contractId: any) {
    return user && user.hasPermission(requiredPermission, contractId);
}
function common(vm: CommonStatusControllerState,
                $rootScope: StatusRootScope,
                $scope: angular.IScope,
                $routeParams: angular.route.IRouteParamsService,
                $q: angular.IQService,
                $uibModal: angular.ui.bootstrap.IModalService,
                server: server,
                contract: contractService,
                datacatalog: datacatalog,
                security: security,
                nvdbapi: nvdbapi) {
    vm.accordion = {
        completenessIsOpen: false
    };
    vm.nvdbDeletions = [];
    vm.mapDisplay = 'features';

    let map: Map;
    vm.toggleMap = toggleMap;

    // Exposed vars
    vm.contract = contract;
    vm.completenessItems = undefined;
    vm.featureLayers = undefined;
    vm.showFeatures = showFeatures;
    vm.showDeletions = showDeletions;
    vm.showGuide = true;
    vm.highlightedFeatures = [];

    vm.openFeatureTypeModal = openFeatureTypeModal;
    vm.init = init;

    function toggleMap() {
        if (map) {
            map.leafletMap.remove();
            map = null;
        } else {
            initMap();
        }
    }

    function canShowMap(feature: CompletenessItem, destination: Destination) {
        return (destination === 'NVDB' && feature.nvdbObjectCount > 0) || (destination === 'FKB' && feature.fkbObjectCount > 0);
    }

    function openFeatureTypeModal(featuretype: CompletenessItem, destination: Destination) {
        if (!canShowMap(featuretype, destination)) {
            return;
        }

        $uibModal.open({
            animation: true,
            template: featuretypeModalHtml,
            controller: 'FeaturetypeModalController as modalCtrl',
            size: 'fullscreen',
            resolve: {
                featuretype() {
                    return featuretype;
                },
                destination() {
                    return destination;
                },
                onCommentCallbacks() {
                    return [onComment];
                }
            }
        }).result.catch((resp: string) => {
            if (['cancel', 'backdrop click', 'escape key press'].indexOf(resp) === -1) throw resp;
        });
    }

    function unpackFeatures(features: FeatureByTypeId): Feature[] {
        return [].concat.apply([], Object.keys(features).map(function (typeIdStr) {
            return features[parseInt(typeIdStr)];
        }));
    }

    function initMap() {
        map = new Map(document.getElementById('statusmap'));
        // @ts-ignore
        vm.featureLayers = new FeatureLayers($q, map.getLeafletMap(), datacatalog, makeFeatureClickPopup, function () {
            return false;
        }, true, true);
        putFeaturesOnMap("CREATE");
    }

    function makeFeatureClickPopup(id: string, feature: Feature, latlng: L.LatLngExpression, leafletMap: L.Map, datacatalog: datacatalog) {
        const popup = makePopupContent(feature, datacatalog);
        L.popup()
            .setLatLng(latlng)
            .setContent(popup)
            .openOn(leafletMap);
    }

    function makePopupContent(feature: Feature, datacatalog: datacatalog) {
        if (feature.operation == 'CREATE') {
            const destinationText = feature.destination === 'NVDB' ? '' : ' (FKB)';
            const typeIdText = makeFeatureTypeText(feature.typeId, datacatalog.getFeatureTypeName(feature.typeId, feature.destination));
            return typeIdText + `<span>${feature.name}${destinationText}</span></br>`;
        } else if (feature.operation == 'CLOSE') {
            const typeIdText = makeFeatureTypeText(feature.typeId, datacatalog.getFeatureTypeName(feature.typeId, feature.destination));
            return typeIdText + `
                    <span>NVDB-id: ${feature.nvdbId}</span></br>
                    <span>Versjon: ${feature.nvdbVersion}</span></br>
                    `;
        } else {
            throw new Error('Unexpected feature operation (' + feature.operation + ')');
        }
    }

    function makeFeatureTypeText(typeId: number, name: string) {
        return `<span style="font-weight: bold">
                    <span class="color-mid-gray"> ${typeId} </span>
                    <span>${name}</span>
                </span></br>`;
    }

    function showDeletions() {
        vm.highlightedFeatures.length = 0;
        vm.featureLayers?.clear();
        const deletions = vm.nvdbDeletions.filter(deletion => {
            return deletion.geometry.wkt;
        });
        vm.featureLayers?.addWgs84Features(deletions);
    }

    function putFeaturesOnMap(operation: string) {
        const nvdbFeatures = unpackFeatures(vm.contract.featureList?.features);
        const fkbFeatures = unpackFeatures(vm.contract.fkbFeatureList?.features);
        const allFeatures = nvdbFeatures.concat(fkbFeatures);
        let features = allFeatures.filter(feature => {
            return feature.operation == operation;
        });
        vm.featureLayers?.addFeatures(features);
    }

    function showFeatures(operation: string) {
        vm.highlightedFeatures.length = 0;
        vm.featureLayers?.clear();
        putFeaturesOnMap(operation);
    }


    function getAllFeatureTypeComments(contract: contractService): any {
        return angular.merge({}, contract.featureTypeComments, contract.fkbFeatureTypeComments);
    }

    function getDestinationAndFeatureTypeId(comment: Comment) {
        if (comment.objectType === 'FEATURE_TYPE') {
            return comment.objectId;
        } else if (comment.objectType === 'FEATURE') {
            if (comment.destination === 'NVDB') {
                return 'NVDB-' + contract.featureList?.getFeatureById(comment.objectId).typeId;
            } else {
                return 'FKB-' + contract.fkbFeatureList.getFeatureById(comment.objectId).typeId;
            }
        }
    }

    function onComment(comment: Comment) {
        const destinationAndFeatureTypeId = getDestinationAndFeatureTypeId(comment);
        const featureTypes = vm.completenessItems.filter(function (featureType) {
            return 'FKB-' + featureType.fkbFeatureTypeId === destinationAndFeatureTypeId
                || 'NVDB-' + featureType.nvdbFeatureTypeId === destinationAndFeatureTypeId;
        });
        angular.forEach(featureTypes, aggregateFeaturetype);

        vm.allFeatureTypeComments = getAllFeatureTypeComments(contract);
    }

    function aggregateFeaturetype(feature: DisplayCompletenessItem) {
        const issues: ValidationIssue[] = [];
        const featureObjectComments: DisplayFeatureComment[] = [];
        feature.hasNvdb = canShowMap(feature, 'NVDB');
        feature.hasFkb = canShowMap(feature, 'FKB');

        const issueExtractor = function (f: Feature) { // Get both feature and attribute-issues
            issues.push.apply(issues, f.issues);
            angular.forEach(f.attributes, function (a) {
                issues.push.apply(issues, a.issues);
            });

            angular.forEach(f.comments, function (comment) {
                featureObjectComments.push({
                    feature: {id: f.id},
                    name: f.name,
                    comment: comment.comment,
                    error: comment.error
                });
            });
        };

        const featuresByType = contract.featureList?.getFeaturesByType(feature.nvdbFeatureTypeId);
        angular.forEach(featuresByType, issueExtractor);
        const fkbFeaturesByType = contract.fkbFeatureList?.getFeaturesByType(feature.fkbFeatureTypeId);
        angular.forEach(fkbFeaturesByType, issueExtractor);

        feature.comments = featureObjectComments;

        let warningsAndErrors = issues.filter(function (issue) {
            return issue.severity !== 'NOTABENE' && !issue.resolution;
        });

        const featureTypeIssues: ValidationIssue[] = [];
        feature.nvdbFeatureTypeId.forEach((id, i) => {
            featureTypeIssues.push(...getFeatureTypeIssues(id, feature.fkbFeatureTypeId[i]));
        });

        warningsAndErrors = warningsAndErrors.concat(featureTypeIssues);

        feature.issues = warningsAndErrors;
        if (feature.issues.length > 0) {
            feature.issuesByMessage = groupIssuesByMessage(feature.issues);
        }

        feature.hasIssues = hasIssues(feature);
    }

    function getFeatureTypeIssues(nvdbFeatureTypeId: number, fkbFeatureTypeId: number): ValidationIssue[] {
        let issues: ValidationIssue[] = [];
        const nvdbIssues = contract.featureList?.featureTypeIssues[nvdbFeatureTypeId];
        const fkbIssues = contract.fkbFeatureList?.featureTypeIssues[fkbFeatureTypeId];

        if (nvdbIssues) {
            issues = issues.concat(nvdbIssues);
        }
        if (fkbIssues) {
            issues = issues.concat(fkbIssues);
        }

        return issues;
    }

    vm.getFeatureTypeName = getFeatureTypeName;
    vm.getFeatureUri = getFeatureUri;
    vm.toggleHighlightDeletion = toggleHighlightDeletion;
    vm.isHighlighted = isHighlighted;

    function readNvdbDeletions() {
        vm.nvdbDeletions = (unpackFeatures(vm.contract.featureList?.features)).filter(feature => feature.operation == "CLOSE");
        vm.nvdbDeletions.forEach(deletion => {
            deletion.typeName = datacatalog.getFeatureTypeName(deletion.typeId);
        });
    }

    function toggleHighlightDeletion(nvdbDeletion: DeletedFeature) {
        if (vm.mapDisplay != 'deletions') {
            vm.mapDisplay = 'deletions';
            showFeatures("CLOSE");
        }
        if (isHighlighted(nvdbDeletion)) {
            vm.highlightedFeatures.splice(vm.highlightedFeatures.indexOf(nvdbDeletion.id), 1);
        } else {
            vm.highlightedFeatures.push(nvdbDeletion.id);
        }
        vm.featureLayers?.setHighlight(nvdbDeletion.id, isHighlighted(nvdbDeletion));
    }

    function isHighlighted(nvdbDeletion: Feature) {
        return vm.highlightedFeatures.indexOf(nvdbDeletion.id) > -1;
    }

    function getFeatureTypeName(nvdbDeletion: Feature) {
        return datacatalog.getFeatureTypeName(nvdbDeletion.typeId);
    }

    function getFeatureUri(nvdbDeletion: Feature) {
        return nvdbapi.getFeatureUri(nvdbDeletion.typeId, nvdbDeletion.nvdbId);
    }

    function init() {
        return $q.all([
            contract.selectContract($routeParams.contractId, true),
            server.readContractCompleteness($routeParams.contractId),
            server.getFeaturesViewModel($routeParams.contractId),
            security.ensureCurrentUser(),
            contract.readFeatureList(),
            contract.readFKBFeatureList()
        ]).then(function (responses) {
            const contract = responses[0];
            vm.contract = contract;
            const completeness = responses[1];
            const featuresViewModel = responses[2];
            const user = responses[3];

            $rootScope.contractname = contract.name;

            const datastore = featuresViewModel;
            datastore.contract = contract;
            datastore.canLockFeatureType = permissionGranted(user, 'FEATURE_TYPE_LOCK:TOGGLE', $routeParams.contractId);
            datastore.canLockFeature = permissionGranted(user, 'FEATURE_LOCK:TOGGLE', $routeParams.contractId);
            datastore.canDeleteFeature = permissionGranted(user, 'FEATURE:DELETE', $routeParams.contractId);

            vm.allFeatureTypeComments = getAllFeatureTypeComments(contract);

            angular.forEach(completeness, aggregateFeaturetype);
            vm.completenessItems = completeness;

            if (contract.featureList && contract.fkbFeatureList) {
                putFeaturesOnMap("CREATE");
            } else {
                console.error("Feature lists are not loaded correctly.");
            }

            const completeFileList = []
                .concat(contract.nvdbFileList?.files || [])
                .concat(contract.fkbFileList?.files || []);

            const files: { name: string, issues: ValidationIssue[] }[] = [];
            angular.forEach(completeFileList, function (file) {
                const issues = file.issues.filter(function (issue: { location: any; severity: string; }) {
                    const location = issue.location;
                    return (location.featureId === undefined || location.featureId === null) && issue.severity !== 'NOTABENE';
                });
                if (issues.length > 0) {
                    files.push({ name: file.name, issues });
                }
            });

            vm.files = files;

            vm.commentFeatureType = function (featureTypeId: number, featureTypeName: string, destination: Destination) {
                $uibModal.open({
                    animation: true,
                    template: commentModalContent,
                    controller: 'StatusFeatureTypeCommentController',
                    controllerAs: 'commentCtrl',
                    resolve: {
                        featureTypeId() {
                            return featureTypeId;
                        },
                        featureTypeName() {
                            return featureTypeName;
                        },
                        destination() {
                            return destination;
                        },
                        onCommentCallbacks() {
                            return [onComment];
                        }
                    }
                }).result.catch((resp) => {
                    if (['cancel', 'backdrop click', 'escape key press'].indexOf(resp) === -1) throw resp;
                });
            };

            initMap();
            readNvdbDeletions();
        }).catch(function (error) {
            console.error("Error during initialization:", error);
        });
    }

    function clear() {
        vm.files = [];
        vm.allFeatureTypeComments = [];
        vm.completenessItems = [];
        removeMap();
    }

    function removeMap() {
        if (map) {
            map.leafletMap.remove();
        }
    }

    $scope.$on('$destroy', removeMap);
    $scope.$on(AUTH_EVENTS.AUTH_LOGGED_OUT, function () {
        clear();
    });

    return init();
}

export function ViewContractStatusController($rootScope: StatusRootScope,
                                             $scope: angular.IScope,
                                             $routeParams: angular.route.IRouteParamsService,
                                             $q: angular.IQService,
                                             $uibModal: angular.ui.bootstrap.IModalService,
                                             server: server,
                                             contract: contractService,
                                             datacatalog: datacatalog,
                                             security: security,
                                             nvdbapi: nvdbapi) {
    const vm = this;
    vm.showSidemenu = false;
    common(vm, $rootScope, $scope, $routeParams, $q, $uibModal, server, contract, datacatalog, security, nvdbapi);
}

ViewContractStatusController.$inject = ['$rootScope', '$scope', '$routeParams', '$q', '$uibModal', 'server', 'contract', 'datacatalog', 'security', 'nvdbapi'];

export function ContractStatusController($rootScope: StatusRootScope,
                                         $scope: angular.IScope,
                                         $routeParams: angular.route.IRouteParamsService,
                                         $q: angular.IQService,
                                         $uibModal: angular.ui.bootstrap.IModalService,
                                         nvdbapi: nvdbapi,
                                         server: server,
                                         contract: contractService,
                                         datacatalog: datacatalog,
                                         security: security) {
    const vm: ContractStatusControllerState = this;
    vm.canViewShareableLink = false;
    vm.canResolveIssue = false;
    vm.canCommentContract = false;
    vm.canDeleteDeletion = false;
    vm.showSidemenu = true;

    const commonPromise = common(vm, $rootScope, $scope, $routeParams, $q, $uibModal, server, contract, datacatalog, security, nvdbapi);
    $q.all([security.ensureCurrentUser(), commonPromise])
        .then(data => {
            const user = data[0];
            vm.canViewShareableLink = permissionGranted( user, 'CONTRACT:SHARE_LINK', $routeParams.contractId);
            vm.canResolveIssue = permissionGranted(user,'FEATURE:RESOLVE_ISSUE', $routeParams.contractId);
            vm.canCommentContract = permissionGranted(user, 'CONTRACT_COMMENT:CREATE', $routeParams.contractId);
            vm.canDeleteDeletion = permissionGranted(user,'DELETION:DELETE', $routeParams.contractId);
        });

    vm.openIssueModal = openIssueModal;
    vm.openFeatureIssueModal = openFeatureIssueModal;

    vm.removeDeletion = removeDeletion;

    vm.openCommentModal = function () {
        $uibModal.open({
            animation: true,
            template: commentModalContent,
            controller: 'ContractCommentController',
            controllerAs: 'commentCtrl'
        })
            .result.catch((resp: string) => {
            if (['cancel', 'backdrop click', 'escape key press'].indexOf(resp) === -1) throw resp;
        });
    };

    function openIssueModal(issue: ValidationIssue, allFiles: ContractFile[], file: ContractFile) {
        const modalInstance = $uibModal.open({
            animation: true,
            template: issueModalHtml,
            controller: ['$scope', '$uibModalInstance',
                function ($scope: any, $uibModalInstance: angular.ui.bootstrap.IModalInstanceService) {
                    $scope.issue = issue;
                    $scope.file = file;
                    $scope.severity = issue.severity === 'ERROR' ? 'Feil' : (issue.severity === 'WARNING' ? 'Advarsel' : 'Notabene');

                    $scope.ok = function () {
                        $uibModalInstance.close({type: 'one', issue});
                    };

                    $scope.all = function () {

                        $uibModalInstance.close({type: 'all', issue});
                    };

                    $scope.cancel = function () {
                        $uibModalInstance.dismiss('cancel');
                    };
                }
            ]
        });

        modalInstance.result.then(function (choice: {type: string, issue: ValidationIssue}) {
            const issueIdsToResolve = [];

            if (choice.type === 'one') {
                markNotRelevant(choice.issue);
            } else if (choice.type === 'all') {
                const issues = allFiles.map(function (file) {
                    return file.issues;
                });
                const fileIssues = [].concat.apply([], issues);
                const chosenIssue = choice.issue;

                angular.forEach(fileIssues, function (issue) {
                    if (sameIssue(issue, chosenIssue)) {
                        markNotRelevant(issue);
                    }
                });
            }

            if (issueIdsToResolve.length > 0) contract.resolveIssuesAsNotApplicable(choice.issue.id, true);

            function markNotRelevant(issue: ValidationIssue) {
                issue.resolution = issue.resolution || {} as IssueResolution;
                issue.resolution.code = 'IKKE_RELEVANT';
                issueIdsToResolve.push(issue.id);
            }

            function sameIssue(issue1: ValidationIssue, issue2: ValidationIssue) {
                if (issue1.id === issue2.id) {
                    return true;
                } else {
                    const messageExpression = /(KURVE|FLATE|PUNKT) \d+/; // Fjern KURVE 1 og lignende for å treffe på resten av meldingen.
                    const message1 = issue1.message.replace(messageExpression, '');
                    const message2 = issue2.message.replace(messageExpression, '');
                    return message1 === message2;
                }
            }
        }, function () {
            // XXX Swallows error
        });
    }

    function openFeatureIssueModal(issues: ValidationIssue[], feature: DisplayCompletenessItem) {
        const modalInstance = $uibModal.open({
            animation: true,
            template: featureIssueModalHtml,
            controller: ['$scope', '$uibModalInstance',
                function ($scope: any, $uibModalInstance: angular.ui.bootstrap.IModalInstanceService) {
                    $scope.issues = issues;

                    $scope.ok = function () {
                        $uibModalInstance.close(issues);
                    };

                    $scope.cancel = function () {
                        $uibModalInstance.dismiss('cancel');
                    };
                }
            ]
        });
        modalInstance.result.then((issuesGroups: IssuesByMessage) => {
            contract.resolveIssuesAsNotApplicable(issuesGroups.issueIds[0], true)
                .then(() => {
                    issuesGroups.issues.forEach(issue => {
                        issue.resolution = issue.resolution || {} as IssueResolution;
                        issue.resolution.code = 'IKKE_RELEVANT';
                    });
                    feature.issuesByMessage = feature.issuesByMessage
                        .filter(issue => issue.message !== issuesGroups.message);
                    feature.issues = feature.issues
                        .filter(issue => !issue.resolution);
                    feature.hasIssues = hasIssues(feature);
                });
        });
    }

    function removeDeletion(nvdbId: number) {
        server.removeNvdbDeletion(vm.contract.contractId, nvdbId);
        vm.nvdbDeletions = vm.nvdbDeletions.filter(nvdbDeletion => nvdbDeletion.nvdbId !== nvdbId);
        if (vm.mapDisplay === 'deletions') {
            vm.showDeletions();
        }
    }

    function clear() {
        vm.contract = undefined;
    }

    $scope.$on(AUTH_EVENTS.AUTH_LOGGED_IN, function () {
        vm.init();
    });
    $scope.$on(AUTH_EVENTS.AUTH_LOGGED_OUT, function () {
        clear();
    });

}

ContractStatusController.$inject = ['$rootScope', '$scope', '$routeParams', '$q', '$uibModal', 'nvdbapi', 'server', 'contract', 'datacatalog', 'security'];
